import {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react"
import { useTranslation } from "react-i18next"

import Tooltip from "@/components/tooltip/Tooltip"
import {
  ChartData,
  SessionDetailsData,
  ChartMultiple,
  ChartMultipleItem,
  ToolTipContents,
} from "@/app/report/common/constructor"
import { ChartStyle, createChart } from "@/app/report/common/chart/multiple"
import {
  REPORT_SECTION_PADDING,
  REPORT_PADDING,
  DRAWER_WIDTH,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import { getMetricValueStr } from "@/app/report/common/tools"
import { colors } from "@/components/colors"
import { createDrawerStore } from "@/stores/global"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"

import { ExportFileFunctionType, GraphSettings } from "@/app/report/common/type"
import { PDF } from "@/app/report/common/export/pdf"
import { getDateTimeString } from "@/app/report/common/tools"
import {
  CanvasAndFileName,
  exportMultipleChart,
  exportPartialChartWithLegendToSplitted,
} from "@/app/report/common/export/export"
import {
  CHART_RATIO,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
} from "@/app/report/common/export/constants"

import "./multipleChart.scss"

function MultipleChart(
  { chartData }: { loading: boolean; chartData?: ChartData | SessionDetailsData },
  ref: React.ForwardedRef<{
    drawChart: () => void
    exportFile: (args: ExportFileFunctionType) => Promise<void>
  }>,
) {
  const { t } = useTranslation()

  const elementLegendAndChart = useRef<HTMLDivElement>(null)
  const elementLegends = useRef<HTMLDivElement[]>(null)
  const elementChartContainer = useRef<HTMLDivElement>(null)
  const chartYAxisLeft = useRef<HTMLDivElement>(null)
  const chartElements = useRef<HTMLDivElement>(null)
  const chartYAxisRight = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState<number>(0)
  const [chartHeight, setChartHeight] = useState<number>(0)
  const [tooltipTitles, setTooltipTitles] = useState<string[]>([])
  const [tooltipValues, setTooltipValues] = useState<Array<ToolTipContents>>([])
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)
  const elementHighlighted = useRef<HTMLDivElement>(null)
  const svgYAxisLeft = useRef<SVGSVGElement>(null)
  const svgYAxisRight = useRef<SVGSVGElement>(null)
  const { isOpen: isDrawerOpen, isMobile } = createDrawerStore()
  const isMounted = useRef(false)

  const translateKey = useTranslateMetricKey()

  useEffect(() => {
    elementLegends.current = []
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const handleResize = useCallback(() => {
    const containerWidth = document.querySelector(".Container")?.clientWidth
    if (containerWidth) {
      // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
      // store 타이밍 이슈로 window.innerWidth로 직접 체크
      const isMobileScreen = window.innerWidth < 1200
      const drawerOffset = isDrawerOpen && !isMobileScreen ? DRAWER_WIDTH : 0
      const currentChartWidth =
        containerWidth - drawerOffset - REPORT_PADDING - REPORT_SECTION_PADDING - 20

      if (isMounted.current) {
        setChartWidth(currentChartWidth)
        setChartHeight(currentChartWidth * CHART_RATIO)
      }
    }
  }, [isDrawerOpen])

  useEffect(() => {
    window.addEventListener("resize", handleResize)

    // 모바일에서는 drawer 상태 변경 시 resize 하지 않음 (overlay이므로)
    const isMobileScreen = window.innerWidth < 1200
    if (!isMobileScreen) {
      handleResize()
    }

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isDrawerOpen, handleResize])

  useEffect(() => {
    if (chartData) {
      drawChart()
    }
  }, [chartData, chartWidth, chartHeight, t])

  function mousemove() {
    if (!chartData) {
      return
    }
    setTooltipVisible(true)
  }

  function mouseout() {
    if (!chartData) {
      return
    }

    setTooltipVisible(false)

    if (elementHighlighted.current) {
      elementHighlighted.current.style.fillOpacity = "0"
      elementHighlighted.current.style.strokeOpacity = "0"
    }
  }

  function mouseover(e: MouseEvent, index: number, chartMultiple: ChartMultiple) {
    if (!chartData || !chartMultiple || !chartMultiple.charts.length) {
      return
    }
    const targetData =
      chartData instanceof SessionDetailsData ? chartData.dataSession : chartData.data
    if (!targetData) {
      return
    }

    const rowHeader = chartMultiple.charts[0].rows[index].header
    const newTooltipTitles: string[] = []
    const newTooltipValues = new Array<ToolTipContents>()

    if (chartData instanceof ChartData) {
      const backNumber = rowHeader.get("backNumber") ?? rowHeader.get("uniformNumber")
      const playerName = rowHeader.get("playerName") ?? rowHeader.get("name")
      const date = rowHeader.get("date")
      const activityCount = rowHeader.get("activity")

      if (backNumber !== null && backNumber !== undefined && playerName) {
        newTooltipTitles.push(`${backNumber} ${playerName}`)
      } else if (playerName && date) {
        newTooltipTitles.push(`${playerName} (${date})`)
      } else if (playerName) {
        newTooltipTitles.push(`${playerName}`)
      }

      if (activityCount !== null && activityCount !== undefined) {
        newTooltipValues.push(
          new ToolTipContents({
            title: t("activity"),
            value: `${activityCount}`,
          }),
        )
      }

      chartData.axisY.forEach((y, indexColumn) => {
        const value = chartMultiple.charts[0].rows[index].data[indexColumn]
        if (!Number.isNaN(value)) {
          const { calcType, decimalPlace } = chartData.settings || {}
          const { unit, decimalPoint } = y
          const valueText = getMetricValueStr({
            value: value as number,
            unit,
            decimalPoint: decimalPlace ?? decimalPoint ?? 1,
            calcType,
          })
          const newTitle = translateKey(y.key)
          newTooltipValues.push(
            new ToolTipContents({
              title: newTitle,
              value: valueText,
              color: y.color,
            }),
          )
        }
      })

      setTooltipTitles(newTooltipTitles)
      setTooltipValues(newTooltipValues)

      elementHighlighted.current = e.target as HTMLDivElement
      if (elementHighlighted.current) {
        elementHighlighted.current.style.fill = "red"
        elementHighlighted.current.style.fillOpacity = "0.1"
        elementHighlighted.current.style.stroke = "red"
        elementHighlighted.current.style.strokeWidth = "1"
        elementHighlighted.current.style.strokeOpacity = "0.4"
      }
      return
    }

    const sessionName = rowHeader.get("sessionName") as string
    newTooltipTitles.push(`${sessionName}`)

    const duration = rowHeader.get("duration")
    if (duration !== null && duration !== undefined) {
      const durationMinutes = duration as number
      const hours = Math.floor(durationMinutes / 60)
      const minutes = Math.floor(durationMinutes % 60)
      const seconds = Math.floor((durationMinutes % 1) * 60)

      let durationText = ""
      if (hours > 0) {
        durationText += `${hours}h `
      }
      if (minutes > 0) {
        durationText += `${minutes}' `
      }
      if (seconds > 0 || durationText === "") {
        durationText += `${seconds}''`
      }

      newTooltipValues.push(
        new ToolTipContents({
          title: t("duration"),
          value: durationText.trim(),
        }),
      )
    }

    const playerCount = rowHeader.get("playerCount")
    if (playerCount !== null && playerCount !== undefined) {
      newTooltipValues.push(
        new ToolTipContents({
          title: t("playerCount"),
          value: `${playerCount}`,
        }),
      )
    }

    chartData.axisY.forEach((y, indexColumn) => {
      const value = chartMultiple.charts[0].rows[index].data[indexColumn]
      if (!Number.isNaN(value)) {
        const { calcType, decimalPlace } = chartData.settings || {}
        const { unit, decimalPoint } = y
        const valueText = getMetricValueStr({
          value: value as number,
          unit,
          decimalPoint: decimalPlace ?? decimalPoint ?? 1,
          calcType,
        })
        const newTitle = translateKey(y.key)
        newTooltipValues.push(
          new ToolTipContents({
            title: newTitle,
            value: valueText,
            color: y.color,
          }),
        )
      }
    })
    setTooltipTitles(newTooltipTitles)
    setTooltipValues(newTooltipValues)

    elementHighlighted.current = e.target as HTMLDivElement
    if (elementHighlighted.current) {
      elementHighlighted.current.style.fill = "red"
      elementHighlighted.current.style.fillOpacity = "0.1"
      elementHighlighted.current.style.stroke = "red"
      elementHighlighted.current.style.strokeWidth = "1"
      elementHighlighted.current.style.strokeOpacity = "0.4"
    }
  }

  const drawChart = useCallback(async () => {
    if (
      !chartData ||
      !elementChartContainer ||
      !chartYAxisLeft ||
      !chartElements ||
      !chartYAxisRight
    ) {
      return
    }

    const targetData =
      chartData instanceof SessionDetailsData ? chartData.dataSession : chartData.data
    const maxLabelLength = targetData.reduce((max, row) => {
      const originalLength = row.name?.length || 0
      const displayLength = originalLength > 20 ? 28 : originalLength

      return Math.max(max, displayLength)
    }, 0)

    const estimatedLabelWidth = maxLabelLength * 6.5
    const rotatedHeight = Math.ceil(estimatedLabelWidth * 0.707)
    const xAxisLabelHeight = Math.max(80, rotatedHeight + 20)

    const existingLeftOffset = 20
    const rawOverflow = Math.ceil(estimatedLabelWidth * 0.5)
    const labelOverflowLeft = Math.max(0, rawOverflow - existingLeftOffset)

    const baseHeight = chartHeight || DEFAULT_CHART_HEIGHT
    const additionalHeight = Math.max(0, xAxisLabelHeight - 80)
    const totalHeight = baseHeight + additionalHeight

    const adjustedChartWidth = (chartWidth || DEFAULT_CHART_WIDTH) - labelOverflowLeft

    const chartStyle = new ChartStyle({
      width: adjustedChartWidth,
      height: totalHeight,
      flagShowChartLabelOnXAxis: false,
      flagLongNameOnXAxis: true,
    })
    chartStyle.space.xAxisLabelHeight = xAxisLabelHeight

    // 템플릿의 값 표시 설정을 화면에도 반영한다. 2026-10-01 에 "화면은 툴팁으로"로 바꾸면서
    // 설정 선택기와 함께 끊겼는데, 설정만 되살리면 "설정했는데 화면에 안 나온다"가 된다 (2026-10-15)
    if (chartData.settings?.dataLabel) {
      const dataLabelsMapping: Record<string, VALUE_POSITION_TYPE> = {
        hideValues: VALUE_POSITION_TYPE.HIDE_VALUES,
        abovePlots: VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION,
        separateTable: VALUE_POSITION_TYPE.ABOVE,
      }
      chartStyle.valuePositionType =
        dataLabelsMapping[chartData.settings.dataLabel] || VALUE_POSITION_TYPE.HIDE_VALUES
    } else {
      chartStyle.valuePositionType = VALUE_POSITION_TYPE.HIDE_VALUES
    }

    const charts = [new ChartMultipleItem("chart", null, targetData)]
    const chartMultiple = new ChartMultiple(chartData.axisY, charts)

    const result = createChart({
      chartMultiple,
      style: chartStyle,
      mouseover: (e: MouseEvent, index: number) => mouseover(e, index, chartMultiple),
      mousemove,
      mouseout,
      translateKey,
      decimalPointForExportFile: chartData.settings?.decimalPlace ?? 1,
      t,
    })
    if (
      result &&
      elementChartContainer.current &&
      chartYAxisLeft.current &&
      chartElements.current &&
      chartYAxisRight.current
    ) {
      chartElements.current.innerHTML = ""
      chartYAxisLeft.current.innerHTML = ""
      chartYAxisRight.current.innerHTML = ""

      if (result.svgYAxisLeft) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisLeft.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisLeft.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisLeft)
        chartYAxisLeft.current.appendChild(nodeDiv)
        svgYAxisLeft.current = result.svgYAxisLeft
      }
      if (result.svgYAxisRight) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisRight.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisRight.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisRight)
        chartYAxisRight.current.appendChild(nodeDiv)
        svgYAxisRight.current = result.svgYAxisRight
      }

      const svgCharts: SVGSVGElement[] = []

      if (chartElements.current) {
        chartElements.current.style.paddingLeft = `${labelOverflowLeft}px`
      }

      result.svgCharts.forEach((svgChart) => {
        if (svgChart) {
          const nodeDiv = document.createElement("div")
          nodeDiv.style.width = `${svgChart.viewBox.baseVal.width}px`
          nodeDiv.style.height = `${svgChart.viewBox.baseVal.height}px`
          nodeDiv.appendChild(svgChart)
          chartElements.current?.appendChild(nodeDiv)
          svgCharts.push(svgChart)
        }
      })
    }
  }, [chartData, chartWidth, chartHeight])

  const fileNameAppend = useMemo(() => {
    if (!chartData) {
      return ""
    }
    if (chartData instanceof ChartData) {
      return (chartData.settings as GraphSettings).title.replace(/\s+/g, "_").toLowerCase()
    }
    if (chartData instanceof SessionDetailsData) {
      return "chart_session"
    }
  }, [chartData])

  const exportFile = async (args: ExportFileFunctionType) => {
    const { exportType, magnification, reportType, chartAnnotation, decimalPoints } = args
    const pdf = await PDF.create()
    const FILE_NAME_HEADER = `${reportType}_report_${fileNameAppend}`
    if (
      !elementLegendAndChart.current ||
      !elementLegends.current ||
      !chartData ||
      !chartData.axisY ||
      !chartData.settings
    ) {
      return
    }

    const targetData =
      chartData instanceof SessionDetailsData ? chartData.dataSession : chartData.data
    const charts = [new ChartMultipleItem("chart", null, targetData)]

    const chartMultiple = new ChartMultiple(chartData.axisY, charts)

    const legends = elementLegends.current.map((elementLegendItem, index) => {
      const node = elementLegendItem as HTMLDivElement
      const left = node.offsetLeft - (elementLegendAndChart.current?.offsetLeft || 0)

      return {
        text: translateKey(chartMultiple.axisY[index].key),
        color: chartMultiple.axisY[index].color,
        left: left,
      }
    })

    const chartSizeForExport = { width: 1200, height: 400 }
    const chartStyle = new ChartStyle({
      width: chartSizeForExport.width,
      height: chartSizeForExport.height,
      flagShowChartLabelOnXAxis: false,
      flagLongNameOnXAxis: true,
      magnification,
    })

    chartStyle.valuePositionType = chartAnnotation as VALUE_POSITION_TYPE

    const result = createChart({
      chartMultiple,
      style: chartStyle,
      mouseover: (e: MouseEvent, index: number) => mouseover(e, index, chartMultiple),
      mousemove,
      mouseout,
      translateKey,
      decimalPointForExportFile: decimalPoints,
      t,
    })

    const promises = result?.svgCharts.map((svgChart) => {
      const fileName = `${getDateTimeString()}_${FILE_NAME_HEADER}`
      if (result && result.svgYAxisLeft && svgChart) {
        return exportPartialChartWithLegendToSplitted(
          svgChart,
          result.svgYAxisLeft,
          result?.svgYAxisRight,
          legends,
          fileName,
          chartStyle,
          "white",
          exportType,
          true,
          pdf,
        )
      } else {
        return null
      }
    })

    if (promises !== undefined) {
      Promise.all(promises).then((results: Array<CanvasAndFileName[] | null>) => {
        exportMultipleChart(exportType, results, FILE_NAME_HEADER, pdf)
      })
    }
  }

  useImperativeHandle(ref, () => ({
    drawChart,
    exportFile: async (args: ExportFileFunctionType) => await exportFile(args),
  }))

  return (
    <Tooltip
      type="chart"
      label={tooltipTitles[0]}
      title={
        <div className="bar-chart-tooltip">
          {tooltipValues.map((value, index) => {
            return (
              <div key={index} className="bar-chart-tooltip-item">
                {value.color && (
                  <span
                    style={{
                      backgroundColor: value.color || colors.neutral.black,
                      color: value.color || colors.neutral.black,
                    }}
                  />
                )}
                <p>{`${value.title || ""} ${value.value ? ":" : ""}`}</p>
                {value.value && <p>{value.value}</p>}
              </div>
            )
          })}
        </div>
      }
      size="medium"
      open={tooltipVisible}>
      <div className="chart">
        <div ref={elementLegendAndChart} className="element-legend-and-chart">
          <div className="legend">
            {chartData?.axisY?.map((y, i) => {
              const newAxisKey = translateKey(y.key)

              return (
                <div
                  ref={(el) => {
                    if (el && elementLegends.current) {
                      elementLegends.current[i] = el
                    }
                  }}
                  className="element-legend"
                  key={y.key}
                  style={{
                    color: y.color,
                  }}>
                  <span>●</span>
                  <span className="legend-text">{newAxisKey}</span>
                </div>
              )
            })}
          </div>
        </div>
        <div ref={elementChartContainer} className="element-chart-container">
          <table>
            <tbody>
              <tr>
                <td className="chart-y-axis-left">
                  <div ref={chartYAxisLeft}></div>
                </td>
                <td>
                  <div ref={chartElements} className="chart-elements"></div>
                </td>
                <td className="chart-y-axis-right">
                  <div ref={chartYAxisRight}></div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Tooltip>
  )
}

export default forwardRef(MultipleChart)
